import { Component, Input, Output, EventEmitter } from "@angular/core";
import { CommonModule, CurrencyPipe } from "@angular/common";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { environment } from '../../../../../environments/environment';

export interface CustomEvent {
  type: "donation" | "sale";
  data: any;
}

@Component({
  standalone: true,
  selector: "app-alert-display",
  templateUrl: "./alert-display.component.html",
  styleUrls: ["./alert-display.component.scss"],
  imports: [CommonModule],
  providers: [CurrencyPipe],
  animations: [
    trigger("fadeInOut", [
      state("void", style({ opacity: 0 })),
      transition(":enter", [animate("300ms ease-in", style({ opacity: 1 }))]),
      transition(":leave", [animate("300ms ease-out", style({ opacity: 0 }))]),
    ]),
  ],
})
export class AlertDisplayComponent {
  @Input() event: CustomEvent | null = null;
  @Input() showMessage = false;
  @Input() alertPosition: "top" | "center" | "bottom" = "center";
  @Input() message: string = ""; // 🔄 Mantido para compatibilidade
  
  // 🎯 NOVOS INPUTS PARA TEMPLATE SEPARADO
  @Input() mainText: string = ""; // Ex: "João doou R$ 25,00."
  @Input() userMessage: string = ""; // Ex: "Obrigado pelo stream!"
  @Input() useSeparateTemplate: boolean = false; // Flag para usar template novo
  
  // 🔊 Volume configurável (0-100)
  @Input() alertVolume: number = 70; // Default 70%
  @Input() stopSignal: number = 0; // Increment to force stop
  @Input() voiceRecordingUrl: string | null = null; // URL do áudio customizado
  @Input() streamerId: string = ''; // 🎵 StreamerId para processar áudio Moblin/iOS
  @Input() donationId: string = ''; // 🎵 DonationId para processar áudio Moblin/iOS
  @Input() musicthonConfig: any = null; // 🎵 Config do Musicthon para saber se usa mobile gain
  
  @Output() audioFinished = new EventEmitter<void>();

  get isDonation() {
    return this.event?.type === "donation";
  }

  get isSale() {
    return this.event?.type === "sale";
  }

  private audio?: HTMLAudioElement;
  private audioTimeoutId?: any;
  private audioStarted = false;
  private startAttemptTimer?: any;

  ngOnChanges(changes: any): void {
    if (changes.stopSignal && !changes.stopSignal.firstChange) {
      this.stopCurrentAudio();
    }
    // Fallback: se showMessage mudou para true e a animação não disparar, iniciar áudio mesmo assim.
    if (changes.showMessage && changes.showMessage.currentValue === true) {
      this.startAudioIfNeeded('ngOnChanges');
      // Segunda tentativa assíncrona caso a view ainda não tenha aplicado bindings
      clearTimeout(this.startAttemptTimer);
      this.startAttemptTimer = setTimeout(() => {
        this.startAudioIfNeeded('delayed-start');
      }, 50);
    }
  }

  onAnimationStart(event: any) {
    if (event.toState !== "void" && this.event) {
      this.startAudioIfNeeded();
    }
  }

  onAnimationDone(event: any) {
    if (event.toState === "void" && this.audio) {
      this.stopCurrentAudio();
    }
  }

  private clearAudioTimeout(): void {
    if (this.audioTimeoutId) {
      clearTimeout(this.audioTimeoutId);
      this.audioTimeoutId = undefined;
    }
  }

  private stopCurrentAudio(): void {
    this.clearAudioTimeout();
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio = undefined;
    }
    this.audioStarted = false;
  }

  private startAudioIfNeeded(reason: string = 'unknown'): void {
    if (!this.event) {
      return;
    }
    if (this.audioStarted) {
      return;
    }

    // 🎵 Definir qual áudio usar: steal sfx, customizado ou padrão
    const defaultSound = "assets/sounds/coin.wav";
    
    // Se tem roubo e stealSfxUrl, usa o steal sfx ao invés do áudio customizado
    const eventData = this.event?.data || {};
    const isSteal = eventData.isSteal === true;
    const stealSfxUrl = eventData.stealSfxUrl;
    
    let soundFile = defaultSound;
    let isCustomAudio = false; // Flag para saber se é áudio customizado
    
    if (isSteal && stealSfxUrl) {
      // Roubo: usa steal sfx
      soundFile = this.processAudioUrlForMoblin(stealSfxUrl, 'steal');
      isCustomAudio = true;
      console.log('🎵 [AlertDisplay] Usando steal sfx:', { stealSfxUrl, soundFile, useMobileGain: this.musicthonConfig?.useMobileGain });
    } else if (this.voiceRecordingUrl) {
      // Doação com áudio customizado: usa voiceRecordingUrl
      soundFile = this.processAudioUrlForMoblin(this.voiceRecordingUrl, 'coin');
      isCustomAudio = true;
      console.log('🎵 [AlertDisplay] Usando voiceRecordingUrl:', { voiceRecordingUrl: this.voiceRecordingUrl, soundFile, useMobileGain: this.musicthonConfig?.useMobileGain });
    } else {
      // Usar coin.wav padrão
      soundFile = this.processAudioUrlForMoblin(defaultSound, 'coin');
      console.log('🎵 [AlertDisplay] Usando coin.wav padrão:', { defaultSound, soundFile, useMobileGain: this.musicthonConfig?.useMobileGain });
    }

    console.log('🎵 [AlertDisplay] Criando áudio com src:', soundFile);
    
    // 🔒 Capturar variáveis no closure para uso nos event listeners
    const _isCustomAudio = isCustomAudio;
    const _defaultSound = defaultSound;
    
    // 🌐 Converter URL relativa para absoluta se for endpoint processado
    // IMPORTANTE: Bypassa o proxy do Angular que não lida bem com áudio binário
    let finalAudioUrl = soundFile;
    if (soundFile.startsWith('/api/v1/audio/processed/')) {
      // URL do endpoint de processamento - usar backend direto
      const backendUrl = environment.backend.replace('/api/v1', '');
      finalAudioUrl = `${backendUrl}${soundFile}`;
      console.log('🌐 [AlertDisplay] Convertendo para URL absoluta (bypass proxy):', finalAudioUrl);
    }
    
    this.audio = new Audio(finalAudioUrl);
    this.audio.setAttribute('playsinline', '');
    this.audio.autoplay = true;
    this.audioStarted = true;
    
    // 🔊 Configurar volume HTML5 APENAS se gain não estiver ativo
    // No iOS/Moblin, audio.volume pode cancelar o efeito do FFmpeg
    const useMobileGain = this.musicthonConfig?.useMobileGain === true;
    if (!useMobileGain) {
      this.audio.volume = this.alertVolume / 100;
      console.log(`🔊 [AlertDisplay] Volume HTML5 configurado: ${this.alertVolume}%`);
    } else {
      this.audio.volume = 1.0; // Máximo - ganho já aplicado pelo FFmpeg
      console.log(`🎚️ [AlertDisplay] Gain ativo - volume HTML5 = 100% (FFmpeg aplicou ${this.alertVolume}% como ganho)`);
    }
    
    // 📊 Logs de ciclo de vida do áudio
    this.audio.addEventListener('loadstart', () => {
      console.log('📥 [AlertDisplay] Áudio: loadstart - iniciando carregamento de:', soundFile);
    });
    
    this.audio.addEventListener('loadeddata', () => {
      console.log('✅ [AlertDisplay] Áudio: loadeddata - dados carregados com sucesso');
    });
    
    this.audio.addEventListener('canplay', () => {
      console.log('▶️ [AlertDisplay] Áudio: canplay - pronto para tocar');
    });
    
    this.audio.addEventListener('playing', () => {
      console.log('🔊 [AlertDisplay] Áudio: playing - reproduzindo agora');
    });
    
    // ⏰ Timeout de segurança: forçar emissão após 10 segundos se áudio não terminar
    this.audioTimeoutId = setTimeout(() => {
      this.audioFinished.emit();
      this.clearAudioTimeout();
      this.audioStarted = false;
    }, 10000);
    
    // Emitir evento quando o áudio terminar
    this.audio.addEventListener('ended', () => {
      console.log('⏹️ [AlertDisplay] Áudio: ended - reprodução finalizada');
      this.clearAudioTimeout();
      this.audioFinished.emit();
      this.audioStarted = false;
    });
    
    // 🔄 Se for áudio customizado e der erro, tentar fallback para coin.wav
    this.audio.addEventListener('error', async (err) => {
      console.error('❌ [AlertDisplay] Áudio: ERROR detectado!', {
        soundFile,
        isCustomAudio: _isCustomAudio,
        useMobileGain,
        audioError: this.audio?.error,
        errorCode: this.audio?.error?.code,
        errorMessage: this.audio?.error?.message
      });
      
      // 🔍 Se for endpoint processado, tentar buscar mais detalhes do erro
      if (soundFile.includes('/audio/processed/')) {
        try {
          console.log('🔍 [AlertDisplay] Tentando buscar endpoint para ver erro HTTP...');
          const response = await fetch(soundFile);
          console.log('📡 [AlertDisplay] Response HTTP:', {
            status: response.status,
            statusText: response.statusText,
            contentType: response.headers.get('content-type')
          });
          
          if (!response.ok) {
            const errorBody = await response.text();
            console.error('❌ [AlertDisplay] Resposta do backend:', {
              status: response.status,
              body: errorBody
            });
          }
        } catch (fetchError) {
          console.error('❌ [AlertDisplay] Erro ao buscar endpoint:', fetchError);
        }
      }
      
      if (_isCustomAudio && !this.audio?.src.includes('coin.wav')) {
        console.warn('⚠️ [AlertDisplay] FALLBACK: Áudio customizado falhou, tentando coin.wav padrão...');
        this.audioStarted = false;
        this.clearAudioTimeout();
        
        // Tentar com o áudio padrão
        console.log('🔄 [AlertDisplay] Criando novo áudio com fallback:', _defaultSound);
        this.audio = new Audio(_defaultSound);
        this.audio.setAttribute('playsinline', '');
        this.audio.autoplay = true;
        this.audioStarted = true;
        this.audio.volume = this.alertVolume / 100;
        
        // Timeout de segurança para o fallback
        this.audioTimeoutId = setTimeout(() => {
          console.log('⏰ [AlertDisplay] FALLBACK: Timeout atingido');
          this.audioFinished.emit();
          this.clearAudioTimeout();
          this.audioStarted = false;
        }, 10000);
        
        this.audio.addEventListener('ended', () => {
          console.log('⏹️ [AlertDisplay] FALLBACK: Áudio ended');
          this.clearAudioTimeout();
          this.audioFinished.emit();
          this.audioStarted = false;
        });
        
        this.audio.addEventListener('error', () => {
          console.error('❌ [AlertDisplay] FALLBACK: coin.wav TAMBÉM FALHOU!');
          this.clearAudioTimeout();
          this.audioFinished.emit();
          this.audioStarted = false;
        });
        
        this.audio.play().catch((err) => {
          console.error('❌ [AlertDisplay] FALLBACK: Erro ao dar play:', err);
          this.clearAudioTimeout();
          this.audioFinished.emit();
          this.audioStarted = false;
        });
      } else {
        console.error('❌ [AlertDisplay] Erro no áudio, mas não é customizado ou já é coin.wav - não tentando fallback');
        // ⚠️ Emitir evento mesmo com erro para não bloquear TTS
        this.clearAudioTimeout();
        this.audioFinished.emit();
        this.audioStarted = false;
      }
    });
    
    this.audio.play().catch((err) => {
      // ⚠️ Emitir evento mesmo se autoplay bloqueado (teste visual)
      this.clearAudioTimeout();
      this.audioFinished.emit();
      this.audioStarted = false;
    });
  }

  /**
   * 📱 Processar URL de áudio para Moblin/iOS (se useMobileGain estiver ativo)
   */
  private processAudioUrlForMoblin(audioUrl: string, audioType: 'coin' | 'steal' | 'tts' | 'voice_recording'): string {
    // Se useMobileGain não está ativo, retorna URL original
    if (!this.musicthonConfig?.useMobileGain || !this.streamerId || !this.donationId) {
      return audioUrl;
    }

    // Para testes, NÃO processar - usar áudio original
    if (this.donationId?.startsWith('test-')) {
      return audioUrl;
    }

    // Se a URL é local (assets), não processar
    if (audioUrl.startsWith('assets/')) {
      return audioUrl;
    }

    // Se a URL já é do endpoint de processamento, retorna sem modificar
    if (audioUrl.includes('/audio/processed/')) {
      return audioUrl;
    }

    // Construir URL do endpoint de processamento dinâmico
    const processedUrl = `/api/v1/audio/processed/${this.donationId}?audioType=${audioType}&streamerId=${this.streamerId}`;
    
    console.log('📱 [AlertDisplay] Moblin/iOS gain ativo - usando endpoint processado', {
      original: audioUrl,
      processed: processedUrl,
      audioType,
      streamerId: this.streamerId
    });

    return processedUrl;
  }
}
